import React, { useCallback, useState, useEffect } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Formik, FormikHelpers, Form } from 'formik'
import { updateUsersUser2faEnable } from '@racepointenergy/swc/scs/update-users-user-2fa-enable'
import { updateUsersUser2faDisable } from '@racepointenergy/swc/scs/update-users-user-2fa-disable'
import { updateUsersUser2faConfirm } from '@racepointenergy/swc/scs/update-users-user-2fa-confirm'
import {
  TwoFactorTokenType,
  User,
  UserCredentials,
} from '@racepointenergy/swc/scs/types/user'

import { useAppDispatch } from 'app/hooks'
import BaseModal from 'components/Modal/BaseModal'

import {
  getAuthorizedUser,
  storeAuthorizedUser,
} from 'app/services/LocalStorage'
import TwoFaInputs from 'features/auth/login/components/TwoFaForm/TwoFaInputs'

import { H2, Regular, SectionLabel } from 'components/Typography'
import { StyledInputError } from 'components/Input/styles'
import { WhiteBlock } from 'components/Layout'
import { CommonButton } from 'components/Button'
import Toggle from 'components/Input/Toggle'
import { myAccountMessages } from '../messages'
import { I2FaValues } from '../types'
import {
  ChangePassModalContainer,
  StyledButtonsWrapper,
  // StyledResendVerification,
  StyledDescriptionText,
  StyledDescriptionTextWrapper,
  // ResendCodeWrapper,
  // StyledResendText,
  ModalContent,
  StyledInstructionsBlock,
  AccountToggleContainer,
  TwoFactorBlock,
  ToggleWrapper,
} from './styles'
import { ModalTitle } from 'components/Modal/styles'
import { CheckCircleOutline } from 'components/Icons'
import { SpinnerCentered, Spinner } from 'components/Loader'
import theme from 'theme_'
import { updateAuthStatusThunk } from 'features/auth/login/slice'

const TwoFactor: React.FC = () => {
  const dispatch = useAppDispatch()
  const { formatMessage } = useIntl()
  const [dispatchUpdateUsersUser2faEnable] =
    updateUsersUser2faEnable.useMutation()
  const [dispatchUpdateUsersUser2faDisable] =
    updateUsersUser2faDisable.useMutation()
  const [dispatchUpdateUsersUser2faConfirm] =
    updateUsersUser2faConfirm.useMutation()

  const [loading, setIsLoading] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [twoFaEnabled, setTwoFaEnabled] = useState(false)
  const [twoFaCode, setTwoFaCode] = useState('')
  const [twoFaFormError, setTwoFaFormError] = useState<string | null>(null)

  const storedUser = getAuthorizedUser()

  useEffect(() => {
    setTwoFaEnabled(
      (storedUser && storedUser.twoFactor[0]?.enabled === 'Enabled') ?? false
    )
  }, [storedUser])

  const openModal = useCallback(() => {
    setIsModalOpen(true)
  }, [])

  const closeModal = useCallback(() => {
    setIsModalOpen(false)
  }, [setIsModalOpen])

  const onError = (err: any) => {
    setIsLoading(false)
    setTwoFaFormError(formatMessage(myAccountMessages.twoFactorError))
  }

  const handleChange = async (e: any) => {
    if (!storedUser) {
      return
    }

    setIsLoading(true)
    if (twoFaEnabled) {
      dispatchUpdateUsersUser2faDisable({
        userId: storedUser.userId,
        twoFactor: [
          {
            type: TwoFactorTokenType.Email,
          },
        ],
      })
        .unwrap()
        .then(() => {
          setIsLoading(false)
          openModal()
        })
        .catch((error) => {
          onError(error)
        })
    } else {
      dispatchUpdateUsersUser2faEnable({
        userId: storedUser.userId,
        /// @ts-ignore Should SWC have this on User?
        clientId: storedUser.clientId,
        twoFactor: [
          {
            type: TwoFactorTokenType.Email,
          },
        ],
      })
        .unwrap()
        .then(() => {
          setIsLoading(false)
          openModal()
        })
        .catch((error) => {
          onError(error)
        })
    }
  }

  const handleSubmit = async (
    values: I2FaValues,
    formikHelpers: FormikHelpers<I2FaValues>
  ) => {
    if (!storedUser) {
      return
    }
    setIsLoading(true)

    dispatchUpdateUsersUser2faConfirm({
      userId: storedUser.userId,
      /// @ts-ignore Should SWC have this on User?
      clientId: storedUser.clientId,
      twoFactor: [
        {
          code: twoFaCode,
          type: TwoFactorTokenType.Email,
        },
      ],
    })
      .unwrap()
      .then((result) => {
        setIsLoading(false)
        storeAuthorizedUser(result as UserCredentials & User)
        dispatch(updateAuthStatusThunk())
        closeModal()
      })
      .catch((error) => {
        onError(error)
      })
  }

  // const resendClick = () => {
  //   setIsLoading(true)
  //   dispatch(enableTwoFaThunk({ onSuccess: onEnableSuccess, onError }))
  // }

  return (
    <>
      <BaseModal isOpen={isModalOpen} onRequestClose={closeModal}>
        <ChangePassModalContainer>
          <CheckCircleOutline />
          <ModalTitle>
            <H2>{formatMessage(myAccountMessages.changePassModalHeading)}</H2>
          </ModalTitle>
          <Formik initialValues={{ code: '' }} onSubmit={handleSubmit}>
            {(formikProps) => (
              <Form>
                <ModalContent>
                  <StyledDescriptionTextWrapper>
                    <StyledDescriptionText>
                      <FormattedMessage {...myAccountMessages.enterCode} />
                    </StyledDescriptionText>
                  </StyledDescriptionTextWrapper>
                  <TwoFaInputs onChange={setTwoFaCode} />
                  <StyledInputError>{twoFaFormError}</StyledInputError>

                  <StyledButtonsWrapper>
                    <CommonButton
                      type="button"
                      onClick={(ev) => {
                        ev.preventDefault()
                        ev.stopPropagation()
                        closeModal()
                      }}
                      variant="secondary"
                      disabled={formikProps.isSubmitting || loading}
                    >
                      {formatMessage(myAccountMessages.changePassModalClose)}
                    </CommonButton>
                    <CommonButton
                      type="submit"
                      disabled={
                        formikProps.isSubmitting ||
                        twoFaCode.length !== 6 ||
                        !formikProps.isValid ||
                        loading
                      }
                    >
                      {formatMessage(myAccountMessages.verifyCode)}
                    </CommonButton>
                  </StyledButtonsWrapper>
                  {/* <ResendCodeWrapper>
                    <StyledResendText>
                      {formatMessage(myAccountMessages.resendDescription)}{' '}
                    </StyledResendText>
                    <StyledResendVerification to="#" onClick={resendClick}>
                      {formatMessage(myAccountMessages.resendCode)}
                    </StyledResendVerification>
                  </ResendCodeWrapper> */}
                </ModalContent>
              </Form>
            )}
          </Formik>
        </ChangePassModalContainer>
      </BaseModal>
      <WhiteBlock>
        {!storedUser ? (
          <SpinnerCentered />
        ) : (
          <>
            <SectionLabel color={theme.colors.mutedGray}>
              <FormattedMessage {...myAccountMessages.twoFactorHeading} />
            </SectionLabel>
            <StyledInstructionsBlock>
              <Regular>
                <FormattedMessage
                  {...myAccountMessages.twoFactorText}
                  values={{
                    br: <br />,
                  }}
                />
              </Regular>
            </StyledInstructionsBlock>
            <TwoFactorBlock>
              <AccountToggleContainer>
                <Regular>
                  <FormattedMessage {...myAccountMessages.twoFactorToggle} />
                </Regular>
                {loading ? (
                  <Spinner />
                ) : (
                  <ToggleWrapper>
                    <span>
                      {twoFaEnabled
                        ? formatMessage(myAccountMessages.enabled)
                        : formatMessage(myAccountMessages.disabled)}
                    </span>
                    <Toggle
                      checked={twoFaEnabled}
                      disabled={loading}
                      onChange={(e: any) => handleChange(e)}
                    />
                  </ToggleWrapper>
                )}
              </AccountToggleContainer>
            </TwoFactorBlock>
          </>
        )}
      </WhiteBlock>
    </>
  )
}

export default TwoFactor
